import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { InfiniteScroll, Paragraph, Spin } from 'common/components';
import { showSuccessMessage } from 'common/helpers';
import { useSocketContext } from 'common/context';
import { ESocketEvent } from 'common/const';
import { LIST_LIMIT_0 } from 'common/config';
import { Bin, Copy, History } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { FeedCreateModal } from 'entities/Account/components';
import {
  ContractBrandsModal,
  ContractCategoriesModal,
  ContractFeedUrlDeleteModal,
  ContractUnsetManualSettingsModal,
} from 'entities/Contract/components';
import { IContract } from 'entities/Contract/Contract.models';
import { buildTreeKeysFromCategoryList, mapCategoryListToTreeData } from 'entities/Settings/Settings.helper';
import { getBrands, selectBrandList, selectIsBrandLoading } from 'entities/Brand/Brand.slice';
import { getCatalogList, selectIsCatalogLoading } from 'entities/Catalog/Catalog.slice';
import { getCategoryList, selectCategoryList, selectIsCategoryLoading } from 'entities/Category/Category.slice';
import {
  getContractSyncStatus,
  selectIsContractLoading,
  unsetContractManualSettings,
  updateContract,
} from 'entities/Contract/Contract.slice';
import { IUpdateAccountPayload } from 'entities/Account/Account.models';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  contract: IContract | null;
}

export const SettingsContractAssortmentTab: FC<IComponentProps> = ({ header, contract }) => {
  const dispatch = useAppDispatch();
  // brand
  const brandList = useAppSelector(selectBrandList);
  const isBrandLoading = useAppSelector(selectIsBrandLoading);
  // catalog
  const isCatalogLoading = useAppSelector(selectIsCatalogLoading);
  // category
  const categoryList = useAppSelector(selectCategoryList);
  const isCategoryLoading = useAppSelector(selectIsCategoryLoading);
  // contract
  const isContractLoading = useAppSelector(selectIsContractLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openContractCategoriesModal, setOpenContractCategoriesModal] = useState<boolean>(false);
  const [openContractBrandsModal, setOpenContractBrandsModal] = useState<boolean>(false);
  const [openContractUnsetManualSettingsModal, setOpenContractUnsetManualSettingsModal] = useState<boolean>(false);
  const [openContractFeedUrlChangeModal, setOpenContractFeedUrlChangeModal] = useState<boolean>(false);
  const [openContractFeedUrlDeleteModal, setOpenContractFeedUrlDeleteModal] = useState<boolean>(false);
  const [syncInProgress, setSyncInProgress] = useState<boolean>(false);
  const { socket } = useSocketContext();

  const accountId = currentUser?.accountId;
  const loading = fetchLoading || isCatalogLoading || isCategoryLoading || isBrandLoading || isContractLoading;
  const categoryTreeData = useMemo(() => mapCategoryListToTreeData(categoryList), [categoryList]);
  const allCategoryTreeKeys = useMemo(() => buildTreeKeysFromCategoryList(categoryList, null), [categoryList]);

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);

      if (contract) {
        const contractSyncStatusResponse = await dispatch(getContractSyncStatus({ contractId: contract.id }));

        if (contractSyncStatusResponse) {
          setSyncInProgress(contractSyncStatusResponse.inProgress);
        }
      }

      setFetchLoading(false);
    };

    fetch();
  }, [contract?.id]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    const updateSyncInProgress = () => setSyncInProgress(false);

    socket.on(ESocketEvent.ContractSync, updateSyncInProgress);

    return () => {
      if (!socket) {
        return;
      }

      socket.off(ESocketEvent.ContractSync, updateSyncInProgress);
    };
  }, [socket]);

  const onOpenContractCategoriesModal = async () => {
    const catalogListResponse = await dispatch(getCatalogList({ limit: LIST_LIMIT_0 }));

    if (!catalogListResponse) {
      return;
    }

    const catalog = catalogListResponse.find((item) => item.sellerId === accountId);

    if (!catalog) {
      return;
    }

    await dispatch(
      getCategoryList({
        catalogId: catalog.id,
        limit: LIST_LIMIT_0,
        onSuccess: () => setOpenContractCategoriesModal(true),
      }),
    );
  };

  const onContractCategoriesChange = (value: number[] | null, onSuccess: () => void) => {
    if (!contract) {
      return;
    }

    dispatch(
      updateContract({
        id: contract.id,
        availableCategories: value,
        onSuccess: () => {
          onSuccess();
          setSyncInProgress(true);
          showSuccessMessage('Категории успешно изменены.');
        },
      }),
    );
  };

  const onOpenContractBrandsModal = () => {
    dispatch(getBrands({ onSuccess: () => setOpenContractBrandsModal(true) }));
  };

  const onContractBrandsChange = (value: string[] | null, onSuccess: () => void) => {
    if (!contract) {
      return;
    }

    dispatch(
      updateContract({
        id: contract.id,
        availableBrands: value,
        onSuccess: () => {
          onSuccess();
          setSyncInProgress(true);
          showSuccessMessage('Бренды успешно изменены.');
        },
      }),
    );
  };

  const onContractUnsetManualSettingsModalAcceptClick = () => {
    if (!contract) {
      return;
    }

    dispatch(
      unsetContractManualSettings({
        contractId: contract.id,
        onSuccess: () => {
          setSyncInProgress(true);
          setOpenContractUnsetManualSettingsModal(false);
        },
      }),
    );
  };

  const onContractFeedUrlChangeModalSaveClick = ({ onSuccess, feedUrl }: Omit<IUpdateAccountPayload, 'id'>) => {
    if (!contract) {
      return;
    }

    dispatch(
      updateContract({
        id: contract.id,
        feedUrl,
        onSuccess: () => {
          onSuccess?.();
          setSyncInProgress(true);
        },
      }),
    );
  };

  const onContractFeedUrlDeleteModalAcceptClick = () => {
    if (!contract) {
      return;
    }

    dispatch(
      updateContract({
        id: contract.id,
        feedUrl: null,
        onSuccess: () => {
          setSyncInProgress(true);
          setOpenContractFeedUrlDeleteModal(false);
        },
      }),
    );
  };

  return (
    <>
      <InfiniteScroll>
        {header}

        <div>
          <Spin spinning={loading}>
            <div className="mb-14" style={{ maxWidth: 700 }}>
              <div className="mb-3 ff-medium fs-m">Ручная настройка ассортимента</div>

              <div className="mb-6 ff-regular fs-xs color-dark-grey lineH-normal">
                Вы можете самостоятельно настроить ассортимент и бренды, доступные к заказу в рамках контракта. При установленной
                автоматической настройке (кастомный фид) ручные настройки не работают.
              </div>

              {syncInProgress && (
                <div className="mb-6 flex align-center">
                  <div className="mr-3 flex align-center">
                    <History className="icon-history-bright-green" />
                  </div>
                  <span className="ff-medium fs-xs color-bright-green">
                    Изменения применяются, вы сможете обновить настройки после завершения
                  </span>
                </div>
              )}

              <div className="flex align-center">
                <div className="mr-4">
                  <Button
                    className="button-sm secondary"
                    onClick={onOpenContractCategoriesModal}
                    disabled={!!contract?.feedUrl?.length || syncInProgress}
                  >
                    Настроить категории товаров
                  </Button>
                </div>

                <Button
                  className="button-sm secondary"
                  onClick={onOpenContractBrandsModal}
                  disabled={!!contract?.feedUrl?.length || syncInProgress}
                >
                  Настроить бренды
                </Button>
              </div>
            </div>

            <div className="mb-14" style={{ maxWidth: 700 }}>
              <div className="mb-3 ff-medium fs-m">Цена и доступность товаров</div>

              <div className="mb-6 ff-regular fs-xs color-dark-grey lineH-normal">
                Вы можете удалить точечные ручные настройки по всем товарам каталога одновременно.
              </div>

              <Button
                className="button-sm deleting"
                onClick={() => setOpenContractUnsetManualSettingsModal(true)}
                disabled={!!contract?.feedUrl?.length || syncInProgress}
                icon={<Bin className="icon-bin-pale-red" />}
              >
                Сбросить настройки
              </Button>
            </div>

            <div className="mb-14" style={{ maxWidth: 700 }}>
              <div className="mb-3 ff-medium fs-m">Автоматическая настройка ассортимента</div>

              <div className="mb-6 ff-regular fs-xs color-dark-grey lineH-normal">
                Укажите ссылку на кастомный фид для автоматического обновления доступности и цены товаров в вашем каталоге.
                Убедитесь, что данные актуальны и доступны в нужном формате.
              </div>

              {contract?.feedUrl && (
                <div className="mb-6">
                  <Paragraph
                    ellipsis={{ rows: 1, expandable: true }}
                    classNames={{ text: 'ff-medium fs-s', toggle: 'ff-semibold fs-xs' }}
                    color={{ toggle: '#387D61' }}
                    copyable
                    copyIcon={<Copy className="icon-dark-grey cursor-pointer" />}
                  >
                    {contract.feedUrl}
                  </Paragraph>
                </div>
              )}

              <div className="flex align-center">
                <Button
                  className={`button-sm ${contract?.feedUrl?.length ? 'secondary' : 'primary'}`}
                  onClick={() => setOpenContractFeedUrlChangeModal(true)}
                  disabled={syncInProgress}
                >
                  {contract?.feedUrl?.length ? 'Изменить фид' : 'Добавить кастомный фид'}
                </Button>

                {!!contract?.feedUrl?.length && (
                  <div className="ml-4">
                    <Button
                      className="button-circle deleting"
                      icon={<Bin className="icon-bin-pale-red" />}
                      onClick={() => setOpenContractFeedUrlDeleteModal(true)}
                    />
                  </div>
                )}
              </div>
            </div>
          </Spin>
        </div>
      </InfiniteScroll>

      <ContractCategoriesModal
        open={openContractCategoriesModal}
        loading={isContractLoading}
        defaultValue={contract?.availableCategories}
        treeData={categoryTreeData}
        allTreeKeys={allCategoryTreeKeys}
        onClose={() => setOpenContractCategoriesModal(false)}
        onSave={onContractCategoriesChange}
      />

      <ContractBrandsModal
        open={openContractBrandsModal}
        loading={isContractLoading}
        brandList={brandList}
        defaultValue={contract?.availableBrands}
        onClose={() => setOpenContractBrandsModal(false)}
        onSave={onContractBrandsChange}
      />

      <ContractUnsetManualSettingsModal
        open={openContractUnsetManualSettingsModal}
        loading={isContractLoading}
        onClose={() => setOpenContractUnsetManualSettingsModal(false)}
        onAccept={onContractUnsetManualSettingsModalAcceptClick}
      />

      <FeedCreateModal
        open={openContractFeedUrlChangeModal}
        loading={isContractLoading}
        url={contract?.feedUrl}
        onClose={() => setOpenContractFeedUrlChangeModal(false)}
        onSave={onContractFeedUrlChangeModalSaveClick}
      />

      <ContractFeedUrlDeleteModal
        open={openContractFeedUrlDeleteModal}
        loading={isContractLoading}
        onClose={() => setOpenContractFeedUrlDeleteModal(false)}
        onAccept={onContractFeedUrlDeleteModalAcceptClick}
      />
    </>
  );
};
